import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";
import WhatsAppButton from "./WhatsAppButton";

function formatPrice(price) {
  const value = Number(price || 0);
  if (!value) return "Harga belum tersedia";
  return `Rp ${value.toLocaleString("id-ID")}`;
}

export default function ProductDetail({ isAdmin, AdminSection }) {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [selectedImg, setSelectedImg] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProductDetail = async () => {
      try {
        setLoading(true);

        const [detailRes, listRes] = await Promise.all([
          fetch(
            `https://bum-desa-warga-dekat-bandung-production.up.railway.app/api/products/${id}`,
          ),
          fetch(
            "https://bum-desa-warga-dekat-bandung-production.up.railway.app/api/products",
          ),
        ]);

        const detailData = await detailRes.json();
        const listData = await listRes.json();

        if (!detailRes.ok) {
          throw new Error(detailData.error || "Gagal mengambil detail produk");
        }

        const detailProduct = detailData?.data || detailData || null;
        setProduct(detailProduct);

        let productsArray = [];

        if (Array.isArray(listData)) {
          productsArray = listData;
        } else if (Array.isArray(listData.products)) {
          productsArray = listData.products;
        } else if (Array.isArray(listData.data)) {
          productsArray = listData.data;
        }

        setAllProducts(productsArray);
        setSelectedImg(0);
      } catch (err) {
        console.error("Gagal ambil detail produk:", err);
        setProduct(null);
        setAllProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProductDetail();
  }, [id]);

  const productImages = useMemo(() => {
    if (
      product?.images &&
      Array.isArray(product.images) &&
      product.images.length > 0
    ) {
      return product.images;
    }

    if (product?.image_url) return [product.image_url];
    if (product?.img) return [product.img];

    return [];
  }, [product]);

  const relatedProducts = Array.isArray(allProducts)
    ? allProducts.filter((item) => String(item.id) !== String(id)).slice(0, 2)
    : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-white font-serif">
        <Navbar />
        <div className="pt-32 text-center text-gray-500">
          Loading detail produk...
        </div>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white font-serif">
        <Navbar />
        <div className="pt-32 text-center px-6">
          <h1 className="text-2xl text-[#3d4f45] mb-4">
            Produk tidak ditemukan
          </h1>
          <button
            onClick={() => navigate("/products")}
            className="bg-[#3d4f45] text-white px-6 py-3 rounded-full text-sm hover:bg-[#4a5a50] transition"
          >
            Kembali ke Produk
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-serif">
      <Navbar />
      <WhatsAppButton />

      <section className="py-5 px-6 mt-16 bg-[#f5f7f6]">
        <div className="max-w-6xl mx-auto flex items-center gap-2 text-xs text-gray-500 flex-wrap">
          <Link to="/" className="hover:text-[#3d4f45]">
            Home
          </Link>
          <span>/</span>
          <Link to="/products" className="hover:text-[#3d4f45]">
            Produk
          </Link>
          <span>/</span>
          <span className="text-[#3d4f45]">
            {product.name || "Detail Produk"}
          </span>
        </div>
      </section>

      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-14">
            <div>
              <div className="h-[420px] mb-4 rounded-xl overflow-hidden bg-[#b8c5ba] flex items-center justify-center">
                {productImages.length > 0 ? (
                  <img
                    src={productImages[selectedImg]}
                    alt={product.name || "Produk"}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                ) : (
                  <span className="text-gray-500 text-sm">
                    Foto belum tersedia
                  </span>
                )}
              </div>

              {productImages.length > 1 && (
                <div className="grid grid-cols-4 gap-3">
                  {productImages.map((img, i) => (
                    <div
                      key={i}
                      onClick={() => setSelectedImg(i)}
                      className={`h-24 rounded-lg overflow-hidden cursor-pointer bg-[#b8c5ba] transition ${
                        selectedImg === i
                          ? "ring-2 ring-[#3d4f45]"
                          : "opacity-70 hover:opacity-100"
                      }`}
                    >
                      <img
                        src={img}
                        alt={`Preview ${i + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="text-xs text-gray-400 mb-2 tracking-wider">
                {(product.category || "PRODUK").toUpperCase()}
              </div>

              <h1 className="text-4xl mb-4 text-[#3d4f45] font-light">
                {product.name || "Tanpa Nama"}
              </h1>

              <div className="flex items-baseline gap-2 mb-6 flex-wrap">
                <span className="text-3xl font-bold text-[#3d4f45]">
                  {formatPrice(product.price)}
                </span>
                {product.unit && (
                  <span className="text-gray-400 text-sm">{product.unit}</span>
                )}
              </div>

              <p className="text-gray-600 text-sm leading-relaxed mb-8">
                {product.description || "Tidak ada deskripsi produk."}
              </p>

              <div className="space-y-3 mb-8">
                <a
                  href={`https://wa.me/628123456789?text=${encodeURIComponent(
                    `Halo, saya ingin memesan ${product.name || "produk ini"}`,
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-[#3d4f45] text-white text-center px-8 py-3 rounded-full text-xs tracking-widest hover:bg-[#4a5a50] transition"
                >
                  HUBUNGI VIA WHATSAPP
                </a>

                <button
                  type="button"
                  onClick={() => navigate("/products")}
                  className="w-full border-2 border-[#3d4f45] text-[#3d4f45] px-8 py-3 rounded-full text-xs tracking-widest hover:bg-[#3d4f45] hover:text-white transition"
                >
                  KEMBALI KE PRODUK
                </button>
              </div>

              <div className="pt-6 border-t border-gray-100">
                <h3 className="font-semibold mb-4 text-[#3d4f45] text-sm">
                  Detail Produk
                </h3>

                <div className="space-y-2 text-sm">
                  {[
                    ["Kategori", product.category || "-"],
                    ["Stok", product.stock ?? "-"],
                    ["Satuan", product.unit || "-"],
                  ].map(([k, v]) => (
                    <div
                      key={k}
                      className="flex justify-between py-1 border-b border-gray-50"
                    >
                      <span className="text-gray-500">{k}</span>
                      <span className="text-gray-800">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 pt-12 border-t border-gray-100">
            <h2 className="text-2xl mb-6 text-[#3d4f45] font-light">
              Deskripsi Produk
            </h2>
            <p className="text-gray-600 text-sm leading-relaxed mb-4">
              {product.detail ||
                product.description ||
                "Tidak ada detail produk."}
            </p>
          </div>

          <div className="mt-16">
            <h2 className="text-2xl mb-8 text-[#3d4f45] font-light">
              Produk Terkait
            </h2>

            {relatedProducts.length === 0 ? (
              <p className="text-gray-500 text-sm">Belum ada produk terkait.</p>
            ) : (
              <div className="grid md:grid-cols-2 gap-8">
                {relatedProducts.map((r) => (
                  <div
                    key={r.id}
                    className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-lg transition cursor-pointer"
                    onClick={() => {
                      navigate(`/products/${r.id}`);
                      window.scrollTo(0, 0);
                    }}
                  >
                    <div className="h-48 overflow-hidden bg-[#b8c5ba] flex items-center justify-center">
                      {r.image_url || r.img ? (
                        <img
                          src={r.image_url || r.img}
                          alt={r.name || "Produk"}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      ) : (
                        <span className="text-gray-500 text-sm">
                          Foto belum tersedia
                        </span>
                      )}
                    </div>

                    <div className="p-5">
                      <div className="text-xs text-gray-400 mb-1 tracking-wider">
                        {(r.category || "PRODUK").toUpperCase()}
                      </div>

                      <h3 className="text-lg mb-1 text-[#3d4f45]">
                        {r.name || "Tanpa Nama"}
                      </h3>

                      <p className="text-base font-semibold text-[#3d4f45] mb-3">
                        {formatPrice(r.price)}
                      </p>

                      <button
                        type="button"
                        className="w-full bg-[#3d4f45] text-white px-4 py-2 rounded-full text-xs tracking-wider hover:bg-[#4a5a50] transition"
                      >
                        LIHAT DETAIL
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {isAdmin && (
        <div className="max-w-6xl mx-auto px-6 pb-8">
          <hr className="my-8 border-gray-200" />
          <AdminSection />
        </div>
      )}

      <Footer />
    </div>
  );
}
